import { useState, useRef, useEffect, forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import { ReactSession } from "react-client-session";

import axios from "axios";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import sha1 from "sha1";
import "./Login.css";
import show from "../../assets/show.svg";
import hide from "../../assets/hide.svg";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slide,
} from "@mui/material";
import { getSuccessToast } from "../../toast";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [passwordType, setPasswordType] = useState("password");
  const [invalidCredentials, setInvalidCredentials] = useState(false);
  const [uniquePrint, setUniquePrint] = useState();
  const [accessToken, setAccessToken] = useState("");
  const [refreshToken, setRefreshToken] = useState("");
  const [sessionUsername, setSessionUsername] = useState("");
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [activeSessions, setActiveSessions] = useState([]);
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [uiState, setUiState] = useState({
    error: "",
  });

  const inputRef = useRef(null);
  const passwordRef = useRef(null);

  let navigate = useNavigate();

  useEffect(() => {
    inputRef.current.focus();

    if (Number(sessionStorage.getItem("isLoggedIn")) === 1) {
      const role = ReactSession.get("role");
      if (role === "1") {
        navigate("/admin");
      } else if (role === "2") {
        navigate("/supervisor");
      } else if (role === "3") {
        navigate("/operator");
      } else {
        navigate("/");
      }
    }
  }, []);

  // Fetch active sessions when modal opens
  useEffect(() => {
    if (showSessionModal && sessionUsername && accessToken) {
      fetchActiveSessions();
    }
  }, [showSessionModal]);

  localStorage.clear();

  useEffect(() => {
    const fpPromise = FingerprintJS.load();

    const useragent = navigator.userAgent;

    var mainObj = {};

    const hash = async (str) => {
      const print = await sha1(str);
      console.log(print);
      setUniquePrint(print);
    };

    fpPromise
      .then((fp) => fp.get())
      .then((result) => {
        // This is the visitor identifier:
        const visitorId = result.visitorId;
        mainObj["timeZone"] = result.components.timezone.value;
        mainObj["userAgent"] = useragent;
        mainObj["canvas"] = result.components.canvas.value;
        mainObj["screenRes"] = result.components.screenResolution.value;
        mainObj["plugins"] = result.components.plugins.value;
        mainObj["fontsJS"] = result.components.fonts.value;
        mainObj["deviceMemory"] = result.components.deviceMemory.value;
        hash(JSON.stringify(mainObj));
      });
  }, []);

  // const URL = NODE_ENV === "production"
  const inputTypingStyles = {
    background: username !== "" && "white",
    backgroundSize: username !== "" && "0",
    backgroundPosition: username !== "" && "0",
    textIndent: username !== "" && "0",
  };

  const passwordTypingStyles = {
    background: password !== "" && "white",
    backgroundSize: password !== "" && "0",
    backgroundPosition: password !== "" && "0",
    textIndent: password !== "" && "0",
  };

  const togglePasswordHandler = () => {
    setPasswordType(passwordType === "password" ? "text" : "password");
  };

  const handleLogin = async (usernameValue, passwordValue) => {
    setIsLoading(true);
    setInvalidCredentials(false);
    setUiState((prev) => ({ ...prev, error: "" }));

    try {
      const res = await axios.post(`${process.env.REACT_APP_URL}/auth/login`, {
        username: usernameValue,
        password: passwordValue,
        uniquecode: uniquePrint,
      });

      const { authenticatedUser, access, refresh, scan_config } = res.data;

      // Store session data
      ReactSession.set("username", authenticatedUser.username);
      ReactSession.set("role", authenticatedUser.role);
      ReactSession.set("accessToken", access);
      ReactSession.set("refreshToken", refresh);
      ReactSession.set("mog", scan_config.mog);
      ReactSession.set("varii", scan_config.varii);
      sessionStorage.setItem("isLoggedIn", "1");

      // Navigate based on role
      const roleRoutes = { 1: "/admin", 2: "/supervisor", 3: "/operator" };
      navigate(roleRoutes[authenticatedUser.role] || "/");

      return res.data;
    } catch (error) {
      let errorMessage = "Something went wrong. Please try again.";

      if (!navigator.onLine) {
        errorMessage = "No internet connection. Please check your network.";
      } else if (error.response?.data?.code === "session_limit_reached" || error.response?.data?.code === "session_exists") {
        setAccessToken(error.response.data.access);
        setRefreshToken(error.response.data.refresh);
        setSessionUsername(error.response.data.username);
        setShowSessionModal(true);
        errorMessage = error.response.data.detail || errorMessage;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }

      setUiState((prev) => ({ ...prev, error: errorMessage }));
      setInvalidCredentials(true);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    await handleLogin(username, password);
  };

  const fetchActiveSessions = async () => {
    if (!sessionUsername || !accessToken) return;
    setLoadingSessions(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/auth/active-sessions`,
        {
          username: sessionUsername,
          access: accessToken,
        }
      );
      if (response.data.success) {
        setActiveSessions(response.data.sessions);
      }
    } catch (err) {
      console.error("Failed to fetch active sessions:", err);
      setUiState((prev) => ({
        ...prev,
        error: "Failed to fetch session information.",
      }));
    } finally {
      setLoadingSessions(false);
    }
  };

  const handleSessionSelect = (sessionId) => {
    setSelectedSessions((prev) =>
      prev.includes(sessionId)
        ? prev.filter((id) => id !== sessionId)
        : [...prev, sessionId]
    );
  };

  const handleSelectAllSessions = () => {
    if (selectedSessions.length === activeSessions.length) {
      setSelectedSessions([]);
    } else {
      setSelectedSessions(activeSessions.map((s) => s.session_id));
    }
  };

  const handleLogoutSelected = async () => {
    if (selectedSessions.length === 0) return;
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/auth/invalidate-sessions`,
        {
          username: sessionUsername,
          access: accessToken,
          session_ids: selectedSessions,
        }
      );
      if (response.data.success) {
        sessionStorage.clear();
        localStorage.clear();
        await handleLogin(username, password);
        getSuccessToast(
          `Logged in successfully after terminating ${response.data.invalidated_count} session(s).`
        );
      }
    } catch (err) {
      setUiState((prev) => ({
        ...prev,
        error: "Failed to logout selected sessions. Please try again.",
      }));
    } finally {
      setIsLoading(false);
      setShowSessionModal(false);
      setSelectedSessions([]);
    }
  };

  const handleForceLogout = async () => {
    if (!sessionUsername || !accessToken) return;
    setIsLoading(true);
    try {
      const payload = { 
        username: sessionUsername, 
        access: accessToken,
        refresh: refreshToken 
      };
      const logout = await axios.post(
        `${process.env.REACT_APP_URL}/auth/logout-all`,
        payload
      );
      if (logout.data.success) {
        sessionStorage.clear();
        localStorage.clear();

        await handleLogin(username, password);
        getSuccessToast(
          `Logged in successfully after terminating all ${logout.data.invalidated_count || 'other'} session(s).`
        );
      }
    } catch (err) {
      console.error("Logout all error:", err);
      setUiState((prev) => ({
        ...prev,
        error: err.response?.data?.detail || "Failed to logout all sessions. Please try again.",
      }));
    } finally {
      setIsLoading(false);
      setShowSessionModal(false);
    }
  };

  return (
    <div className="login-container">
      <h3 className="login-heading">login</h3>

      <form className="form-container" onSubmit={submitHandler}>
        <div className="login-input-section" style={inputTypingStyles}>
          <label htmlFor="username" className="username-label">
            Username
          </label>
          <div
            className={`user-input-section  ${
              invalidCredentials ? "invalid-username" : ""
            }`}
          >
            <input
              type="text"
              id="username"
              onChange={(e) => setUsername(e.target.value)}
              value={username}
              className="user-input"
              placeholder="Enter Username"
              required
              ref={inputRef}
            />
          </div>
        </div>
        <div className="login-input-section" style={passwordTypingStyles}>
          <label htmlFor="password" className="password-label">
            Password
          </label>
          <div
            className={`
          user-input-section user-password-section ${
            invalidCredentials ? "invalid-password" : ""
          }`}
          >
            <input
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className=" user-input user-password"
              type={passwordType}
              placeholder="Enter Password"
              required
              ref={passwordRef}
            />
            <div className="password-icon">
              <img
                onClick={togglePasswordHandler}
                src={passwordType === "password" ? show : hide}
                alt="password-toggle"
              />
            </div>
          </div>
        </div>
        {invalidCredentials && (
          <small className="invalid-password_message">{uiState.error}</small>
        )}

        {/* <div className="forgot-password-wrapper">
          <Link className="password-forgot_password" to="forgot-password">
            Forgot Password ?
          </Link>
        </div> */}

        <button disabled={isLoading} className="login-btn">
          LOGIN
        </button>
      </form>
      <Box>
        <Dialog
          open={Boolean(showSessionModal)}
          onClose={(event, reason) => {
            if (reason === "backdropClick" || reason === "escapeKeyDown")
              return;
            setShowSessionModal(false);
            document.body.style.overflow = "auto";
          }}
          // closeAfterTransition
          keepMounted={false}
          sx={{
            "& .MuiDialog-paper": {
              width: "650px",
              maxWidth: "90vw",
              borderRadius: "12px",
              padding: "0.5rem 1rem",
              transition: "all 0.3s ease-in-out",
            },
          }}
        >
          <DialogTitle sx={{ fontWeight: 600, fontSize: "1.2rem", pb: 1 }}>
            Maximum Sessions Reached
          </DialogTitle>
          <DialogContent sx={{ pb: 2 }}>
            <Typography sx={{ mb: 2 }}>
              User <strong>{sessionUsername}</strong> has reached the maximum
              number of active sessions.
            </Typography>

            {loadingSessions ? (
              <Box sx={{ textAlign: "center", py: 3 }}>
                <Typography>Loading active sessions...</Typography>
              </Box>
            ) : (
              <>
                <Typography
                  sx={{
                    fontSize: "0.95rem",
                    fontWeight: 600,
                    mb: 1,
                    color: "#333",
                  }}
                >
                  Active Sessions ({activeSessions.length})
                </Typography>

                <Box
                  sx={{
                    maxHeight: "300px",
                    overflowY: "auto",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    mb: 2,
                  }}
                >
                  {activeSessions.length > 0 && (
                    <Box
                      sx={{
                        p: 1.5,
                        borderBottom: "1px solid #e0e0e0",
                        bgcolor: "#f5f5f5",
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                      }}
                      onClick={handleSelectAllSessions}
                    >
                      <input
                        type="checkbox"
                        checked={
                          selectedSessions.length === activeSessions.length &&
                          activeSessions.length > 0
                        }
                        onChange={handleSelectAllSessions}
                        style={{ marginRight: "10px", cursor: "pointer" }}
                      />
                      <Typography sx={{ fontSize: "0.9rem", fontWeight: 600 }}>
                        Select All
                      </Typography>
                    </Box>
                  )}

                  {activeSessions.map((session) => (
                    <Box
                      key={session.session_id}
                      sx={{
                        p: 1.5,
                        borderBottom: "1px solid #e0e0e0",
                        "&:last-child": { borderBottom: "none" },
                        "&:hover": { bgcolor: "#fafafa" },
                        display: "flex",
                        alignItems: "flex-start",
                        cursor: "pointer",
                      }}
                      onClick={() => handleSessionSelect(session.session_id)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedSessions.includes(session.session_id)}
                        onChange={() => handleSessionSelect(session.session_id)}
                        style={{ marginRight: "12px", marginTop: "4px", cursor: "pointer" }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.9rem",
                              fontWeight: 600,
                              color: "#333",
                            }}
                          >
                            {session.device_name}
                          </Typography>
                          {session.is_current && (
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                bgcolor: "#4caf50",
                                color: "white",
                                px: 1,
                                py: 0.25,
                                borderRadius: "4px",
                              }}
                            >
                              Current
                            </Typography>
                          )}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "0.85rem",
                            color: "#666",
                            mb: 0.5,
                          }}
                        >
                          {session.browser_name} • {session.ip_address || "Unknown IP"}
                        </Typography>
                        <Typography sx={{ fontSize: "0.8rem", color: "#999" }}>
                          Last active:{" "}
                          {new Date(session.last_activity).toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>

                <Typography
                  sx={{ fontSize: "0.85rem", color: "text.secondary", mt: 1 }}
                >
                  Select sessions to logout and continue with new login.
                </Typography>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setShowSessionModal(false);
                setSelectedSessions([]);
                setActiveSessions([]);
                document.body.style.overflow = "auto";
              }}
              disabled={isLoading}
              sx={{
                color: "#666",
                borderColor: "#ccc",
                "&:hover": {
                  borderColor: "#999",
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Cancel
            </Button>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                onClick={handleForceLogout}
                disabled={isLoading}
                sx={{
                  color: "#f44336",
                  borderColor: "#f44336",
                  "&:hover": {
                    borderColor: "#d32f2f",
                    backgroundColor: "rgba(244, 67, 54, 0.04)",
                  },
                }}
              >
                {isLoading ? "Processing..." : "Logout All"}
              </Button>
              <Button
                variant="contained"
                onClick={handleLogoutSelected}
                disabled={isLoading || selectedSessions.length === 0}
                sx={{
                  backgroundColor: "#186daa",
                  "&:hover": { backgroundColor: "#145b8d" },
                  "&:disabled": {
                    backgroundColor: "#ccc",
                    color: "#666",
                  },
                }}
              >
                {isLoading
                  ? "Processing..."
                  : `Logout Selected (${selectedSessions.length})`}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      </Box>
    </div>
  );
};

export default Login;
